import React, { useState, useEffect } from "react";
import { useSidebarLayout } from "@/hooks/useSidebarLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Calendar,
  Plus,
  Save,
  Trash2,
  Edit,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from "lucide-react";
import { useAppStore } from "@/lib/store";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CONFIG } from "@/lib/config";

interface TESRecord {
  id?: string;
  accProjectId?: string;
  accHubId?: string;
  accAccountId?: string;
  tenderPackageId?: string;
  tenderPackage?: string;
  packageStatus?: string;
  orderNumber?: string;
  contractType?: string;
  tenderManager?: string;
  tenderDraftStart?: string;
  tenderSubmitDate?: string;
  tenderPeriod?: string;
  tenderReturnDeadline?: string;
  reviewAnalysisPeriod?: string;
  tenderReportDate?: string;
  contractOrder?: string;
  subcontractorPreparationLeadInPeriod?: string;
  originalSiteStartDate?: string;
  actualSiteStartDate?: string;
  // 计划字段
  plannedSubmitDate?: string;
  plannedReturnDeadline?: string;
  plannedContractOrder?: string;
  programmeStatus?: string;
  plannedTenderPeriod?: number; // 天数
  plannedReviewPeriod?: number; // 天数
  plannedLeadTime?: number; // 天数
  plannedSiteStart?: string; // 与 actualSiteStartDate 同步
  status?: string;
  progress?: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

function EditableCell({ value, onChange, type = "text", placeholder, options, recordId, field, pendingChanges, forceValue }: {
  value: string | number | undefined;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  options?: string[];
  recordId?: string;
  field?: string;
  pendingChanges?: Map<string, Record<string, any>>;
  forceValue?: string | number;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value || ''));

  useEffect(() => {
    console.log('🔄 EditableCell value changed:', { field, recordId, value, editValue, forceValue });
    // 优先使用 forceValue，然后是 value
    const actualValue = forceValue !== undefined ? forceValue : value;
    // 只有在不是编辑状态时才更新值，避免编辑时被覆盖
    if (!isEditing) {
      setEditValue(String(actualValue || ''));
    }
  }, [value, forceValue, field, recordId, isEditing]);

  const handleSave = () => {
    console.log('💾 EditableCell saving:', { field, recordId, value: editValue });
    onChange(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(String(value || ''));
    setIsEditing(false);
  };

  if (isEditing) {
    if (options && options.length > 0) {
      return (
        <div className="flex items-center space-x-1">
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSave();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            className="flex-1 h-6 text-xs border-blue-300 focus:border-blue-500 rounded px-2"
            autoFocus
          >
            <option value="">{placeholder || 'Select...'}</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button
            onClick={handleSave}
            className="p-1 text-green-600 hover:text-green-800 transition-colors"
            title="Confirm"
          >
            <Check className="h-3 w-3" />
          </button>
        </div>
      );
    }
    
    return (
      <div className="flex items-center space-x-1">
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
          type={type === 'number' ? 'text' : type} // 移除数字输入框的上下调整按钮
          placeholder={placeholder}
          className="flex-1 h-6 text-xs border-blue-300 focus:border-blue-500"
          autoFocus
        />
        <button
          onClick={handleSave}
          className="p-1 text-green-600 hover:text-green-800 transition-colors"
          title="Confirm"
        >
          <Check className="h-3 w-3" />
        </button>
      </div>
    );
  }

  // 检查是否有待保存的更改
  const hasPendingChanges = recordId && field && pendingChanges?.has(recordId) && pendingChanges.get(recordId)?.[field];
  
      // 优先使用 forceValue，然后是 value
      const displayValue = forceValue !== undefined ? forceValue : value;
      
      return (
        <div 
          className={`cursor-pointer hover:bg-gray-100 p-1 rounded min-h-[32px] flex items-center justify-center ${
            hasPendingChanges ? 'bg-yellow-200 border-2 border-yellow-400' : ''
          }`}
          onClick={() => setIsEditing(true)}
        >
          <span className="text-xs leading-tight">
            {displayValue || '-'}
          </span>
        </div>
      );
}

export default function TES() {
  const { selectedProject } = useAppStore();
  const selectedProjectId = selectedProject?.id;
  const selectedProjectName = selectedProject?.name;
  const [isEditing, setIsEditing] = useState(false);
  const [tesRecords, setTesRecords] = useState<TESRecord[]>([]);
  const [sortField, setSortField] = useState<string | null>('tenderPackage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [pendingChanges, setPendingChanges] = useState<Map<string, Record<string, any>>>(new Map());
  const [showPlanVsActual, setShowPlanVsActual] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  // 使用 useSidebarLayout hook
  useSidebarLayout();

  // 获取 TES 记录
  const { data: tesData, isLoading, error } = useQuery({
    queryKey: ['tes', selectedProjectId],
    queryFn: async () => {
      if (!selectedProjectId) return [];
      
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch TES records');
      }
      const data = await response.json();
      return data.data || [];
    },
    enabled: !!selectedProjectId,
  });

  // 处理排序
  const sortedTesData = React.useMemo(() => {
    if (!tesData) return [];
    
    let sorted = [...tesData];
    
    if (sortField) {
      sorted.sort((a: TESRecord, b: TESRecord) => {
        const aValue = a[sortField as keyof TESRecord] || '';
        const bValue = b[sortField as keyof TESRecord] || '';
        
        // 处理日期排序
        if (sortField.includes('Date') || sortField.includes('Start') || sortField.includes('Deadline')) {
          const aDate = new Date(aValue as string);
          const bDate = new Date(bValue as string);
          if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0;
          if (isNaN(aDate.getTime())) return 1;
          if (isNaN(bDate.getTime())) return -1;
          return sortDirection === 'asc' ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
        }
        
        // 处理字符串排序
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        const result = aStr.localeCompare(bStr);
        return sortDirection === 'asc' ? result : -result;
      });
    } else {
      // 默认按 tenderPackage 字母顺序排序
      sorted.sort((a: TESRecord, b: TESRecord) => {
        const aPackage = a.tenderPackage || '';
        const bPackage = b.tenderPackage || '';
        return aPackage.localeCompare(bPackage);
      });
    }
    
    return sorted;
  }, [tesData, sortField, sortDirection]);

  // 创建 TES 记录
  const createTesMutation = useMutation({
    mutationFn: async (newRecord: Omit<TESRecord, 'id'>) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRecord),
      });
      
      if (!response.ok) {
        throw new Error('Failed to create TES record');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
      toast.success('TES record created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create TES record');
      console.error('Error creating TES record:', error);
    },
  });

  // 批量更新 TES 记录
  const batchUpdateTesMutation = useMutation({
    mutationFn: async (records: TESRecord[]) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}/batch`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ records }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to batch update TES records');
      }
      
      return response.json();
    },
    onSuccess: () => {
      console.log('✅ Batch TES records updated successfully');
      setPendingChanges(new Map());
      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
      toast.success('All changes saved successfully!');
    },
    onError: (error) => {
      console.error('Error batch updating TES records:', error);
      toast.error('Failed to save changes');
    },
  });

  // 更新 TES 记录
  const updateTesMutation = useMutation({
    mutationFn: async ({ id, field, value }: { id: string; field: string; value: string }) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: value }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update TES record');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      console.log('✅ TES record updated successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
      toast.success('TES record updated successfully');
    },
    onError: (error) => {
      console.error('❌ Error updating TES record:', error);
      toast.error('Failed to update TES record');
    },
  });

  // 删除 TES 记录
  const deleteTesMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete TES record');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
      toast.success('TES record deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete TES record');
      console.error('Error deleting TES record:', error);
    },
  });

  useEffect(() => {
    if (tesData) {
      console.log('🔍 TES Data received:', tesData);
      console.log('🔍 First record:', tesData[0]);
      console.log('🔍 Planned Lead Time in target record:', tesData.find(r => r.id === 'b8424450-dbcb-443c-94fe-77533e2bfb07')?.plannedLeadTime);
      
      // 只有在没有待保存的更改时才更新本地状态
      if (pendingChanges.size === 0) {
        console.log('🔄 No pending changes, updating tesRecords from server');
        setTesRecords(tesData);
      } else {
        console.log('🔄 Has pending changes, keeping local state');
      }
    }
  }, [tesData, pendingChanges.size]);

  const handleAddRecord = () => {
    const newRecord: TESRecord = {
      tenderPackage: '',
      packageStatus: 'active',
      accProjectId: selectedProjectId,
    };
    
    createTesMutation.mutate(newRecord);
  };

  const handleUpdate = (id: string, field: string, value: string) => {
    console.log('🔄 Updating TES record:', { id, field, value });
    console.log('🔄 Current tesRecords before update:', tesRecords.find(r => r.id === id)?.[field as keyof TESRecord]);
    
    // 将更改添加到待保存列表中
    setPendingChanges(prev => {
      const newChanges = new Map(prev);
      const recordChanges = newChanges.get(id) || {};
      recordChanges[field] = field === 'plannedLeadTime' || field === 'plannedTenderPeriod' || field === 'plannedReviewPeriod' 
        ? Number(value) 
        : value;
      
      // 如果是 plannedSiteStart 或 period 字段被更新，需要计算相关日期
      if ((field === 'plannedSiteStart' || field === 'plannedTenderPeriod' || field === 'plannedReviewPeriod' || field === 'plannedLeadTime') && value) {
        const record = tesRecords.find(r => r.id === id);
        if (record) {
          const updatedRecord = { ...record, [field]: value };
          const calculatedDates = calculatePlannedDates(updatedRecord);
          
          if (calculatedDates) {
            recordChanges.plannedSubmitDate = calculatedDates.plannedSubmitDate;
            recordChanges.plannedReturnDeadline = calculatedDates.plannedReturnDeadline;
            recordChanges.plannedContractOrder = calculatedDates.plannedContractOrder;
          }
        }
      }
      
      newChanges.set(id, recordChanges);
      return newChanges;
    });
    
    // 立即更新本地状态以提供即时反馈
    setTesRecords(prev => {
      const updated = prev.map(record => {
        if (record.id === id) {
          const updatedRecord = { 
            ...record, 
            [field]: field === 'plannedLeadTime' || field === 'plannedTenderPeriod' || field === 'plannedReviewPeriod' 
              ? Number(value) 
              : value 
          };
          
          // 如果是 plannedSiteStart 或 period 字段被更新，计算相关日期
          if ((field === 'plannedSiteStart' || field === 'plannedTenderPeriod' || field === 'plannedReviewPeriod' || field === 'plannedLeadTime') && value) {
            const calculatedDates = calculatePlannedDates(updatedRecord);
            if (calculatedDates) {
              return {
                ...updatedRecord,
                plannedSubmitDate: calculatedDates.plannedSubmitDate,
                plannedReturnDeadline: calculatedDates.plannedReturnDeadline,
                plannedContractOrder: calculatedDates.plannedContractOrder,
              };
            }
          }
          
          return updatedRecord;
        }
        return record;
      });
      
      console.log('🔄 Updated tesRecords:', updated.find(r => r.id === id)?.[field as keyof TESRecord]);
      console.log('🔄 Full updated record:', updated.find(r => r.id === id));
      return updated;
    });
  };

  const handleBatchSave = async () => {
    if (pendingChanges.size === 0) {
      toast.info('No changes to save');
      return;
    }

    if (isSaving) {
      return; // Prevent multiple saves
    }

    setIsSaving(true);
    console.log('🔄 Saving individual TES records:', pendingChanges.size, 'records');
    
    // 逐个更新记录 - 每个记录一个请求
    const updatePromises: Promise<any>[] = [];
    pendingChanges.forEach((changes, id) => {
      console.log(`🔄 Updating record ${id} with changes:`, changes);
      
      // 为每个记录创建一个更新请求，包含所有字段
      const updateData: Record<string, any> = {};
      Object.entries(changes).forEach(([field, value]) => {
        updateData[field] = value;
      });
      
      updatePromises.push(
        fetch(`${CONFIG.API_BASE_URL}/api/tes/${selectedProjectId}/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updateData),
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to update TES record: ${response.status}`);
          }
          return response.json();
        })
      );
    });

    try {
      await Promise.all(updatePromises);
      console.log('✅ All records updated successfully');
      setPendingChanges(new Map());
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
      toast.success('All changes saved successfully!');
    } catch (error) {
      console.error('❌ Failed to update records:', error);
      toast.error('Failed to save some changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      deleteTesMutation.mutate(id);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-gray-400 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-blue-600 font-bold" />
      : <ChevronDown className="h-3 w-3 text-blue-600 font-bold" />;
  };

  const getSortHeaderClass = (field: string) => {
    const baseClass = "text-center py-3 px-4 font-semibold text-xs cursor-pointer hover:bg-gray-100 select-none";
    const activeClass = "bg-blue-50 text-blue-700 border-b-2 border-blue-300";
    const inactiveClass = "text-gray-700";
    
    return `${baseClass} ${sortField === field ? activeClass : inactiveClass}`;
  };

  // 计算状态基于数据
  const calculateStatus = (record: TESRecord) => {
    const hasDraftStart = record.tenderDraftStart && record.tenderDraftStart !== '-';
    const hasSubmitDate = record.tenderSubmitDate && record.tenderSubmitDate !== '-';
    const hasReturnDeadline = record.tenderReturnDeadline && record.tenderReturnDeadline !== '-';
    const hasContractOrder = record.contractOrder && record.contractOrder !== '-';
    
    if (!hasDraftStart) {
      return 'Draft';
    }
    
    if (hasDraftStart && !hasSubmitDate) {
      return 'Draft';
    }
    
    if (hasSubmitDate && !hasReturnDeadline) {
      return 'Sent';
    }
    
    if (hasReturnDeadline && !hasContractOrder) {
      return 'Returned';
    }
    
    if (hasContractOrder) {
      return 'Order Placed';
    }
    
    return 'Draft';
  };

  // 反向计算计划日期
  const calculatePlannedDates = (record: TESRecord) => {
    const siteStart = record.plannedSiteStart;
    if (!siteStart) return null;

    const siteStartDate = new Date(siteStart);
    const leadTime = record.plannedLeadTime || 60; // 默认60天
    const reviewPeriod = record.plannedReviewPeriod || 14; // 默认14天
    const tenderPeriod = record.plannedTenderPeriod || 14; // 默认14天

    // 反向计算
    const contractOrderDate = new Date(siteStartDate.getTime() - (leadTime * 24 * 60 * 60 * 1000));
    const returnDeadlineDate = new Date(contractOrderDate.getTime() - (reviewPeriod * 24 * 60 * 60 * 1000));
    const submitDate = new Date(returnDeadlineDate.getTime() - (tenderPeriod * 24 * 60 * 60 * 1000));

    return {
      plannedSubmitDate: submitDate.toISOString().split('T')[0],
      plannedReturnDeadline: returnDeadlineDate.toISOString().split('T')[0],
      plannedContractOrder: contractOrderDate.toISOString().split('T')[0],
    };
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value / 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading TES records...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading TES records</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Tender Event Schedule (TES)
        </h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span>TES Records</span>
              <Button
                onClick={() => setShowPlanVsActual(!showPlanVsActual)}
                variant={showPlanVsActual ? "default" : "outline"}
                size="sm"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Plan VS Actual
              </Button>
            </div>
            <div className="flex space-x-2">
              {!isEditing && (
                <Button
                  onClick={() => setIsEditing(true)}
                  variant="default"
                  size="sm"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Table
                </Button>
              )}
              {isEditing && (
                <Button
                  onClick={handleAddRecord}
                  size="sm"
                  disabled={createTesMutation.isPending}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Record
                </Button>
              )}
              {isEditing && (
                <>
                  <Button
                    onClick={handleBatchSave}
                    size="sm"
                    variant="default"
                    disabled={pendingChanges.size === 0 || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes ({pendingChanges.size})
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setPendingChanges(new Map());
                      setIsEditing(false);
                      // 重新获取数据以恢复原始状态
                      queryClient.invalidateQueries({ queryKey: ['tes', selectedProjectId] });
                    }}
                    size="sm"
                    variant="outline"
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel Editing
                  </Button>
                </>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <div className="min-w-full">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-gray-50 border-b border-gray-200">
                  <TableHead 
                    className={getSortHeaderClass('tenderPackage')}
                    onClick={() => handleSort('tenderPackage')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Tender Package</span>
                      {getSortIcon('tenderPackage')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('packageStatus')}
                    onClick={() => handleSort('packageStatus')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Status</span>
                      {getSortIcon('packageStatus')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('orderNumber')}
                    onClick={() => handleSort('orderNumber')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Order #</span>
                      {getSortIcon('orderNumber')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('contractType')}
                    onClick={() => handleSort('contractType')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Contract Type</span>
                      {getSortIcon('contractType')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('tenderManager')}
                    onClick={() => handleSort('tenderManager')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Tender Manager</span>
                      {getSortIcon('tenderManager')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('tenderDraftStart')}
                    onClick={() => handleSort('tenderDraftStart')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Draft Start</span>
                      {getSortIcon('tenderDraftStart')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('tenderSubmitDate')}
                    onClick={() => handleSort('tenderSubmitDate')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Submit Date</span>
                      {getSortIcon('tenderSubmitDate')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center py-3 px-4 font-semibold text-gray-700 text-xs">Tender Period</TableHead>
                  <TableHead 
                    className={getSortHeaderClass('tenderReturnDeadline')}
                    onClick={() => handleSort('tenderReturnDeadline')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Return Deadline</span>
                      {getSortIcon('tenderReturnDeadline')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center py-3 px-4 font-semibold text-gray-700 text-xs">Review Period</TableHead>
                  <TableHead 
                    className={getSortHeaderClass('contractOrder')}
                    onClick={() => handleSort('contractOrder')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Contract & Order</span>
                      {getSortIcon('contractOrder')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('subcontractorPreparationLeadInPeriod')}
                    onClick={() => handleSort('subcontractorPreparationLeadInPeriod')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Lead Time</span>
                      {getSortIcon('subcontractorPreparationLeadInPeriod')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('originalSiteStartDate')}
                    onClick={() => handleSort('originalSiteStartDate')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Planned Site Start</span>
                      {getSortIcon('originalSiteStartDate')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className={getSortHeaderClass('actualSiteStartDate')}
                    onClick={() => handleSort('actualSiteStartDate')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Actual Site Start</span>
                      {getSortIcon('actualSiteStartDate')}
                    </div>
                  </TableHead>
                  {isEditing && (
                    <TableHead className="text-center py-3 px-4 font-semibold text-gray-700 text-xs">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTesData.map((record: TESRecord, index: number) => (
                  <React.Fragment key={record.id || index}>
                    {/* 实际数据行 */}
                    <TableRow className="border-b border-gray-100 hover:bg-gray-50 transition-colors h-16">
                    {/* Tender Package - 只读 */}
                    <TableCell className="py-2 px-3 text-center align-middle align-middle">
                      <div className="font-medium text-gray-900 text-xs leading-tight">{record.tenderPackage || '-'}</div>
                    </TableCell>
                    {/* Package Status - 计算得出，不可编辑 */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (() => {
                          const status = calculateStatus(record);
                          switch (status) {
                            case 'Draft':
                              return 'bg-yellow-100 text-yellow-800';
                            case 'Sent':
                              return 'bg-blue-100 text-blue-800';
                            case 'Returned':
                              return 'bg-purple-100 text-purple-800';
                            case 'Order Placed':
                              return 'bg-green-100 text-green-800';
                            default:
                              return 'bg-gray-100 text-gray-800';
                          }
                        })()
                      }`}>
                        {calculateStatus(record)}
                      </span>
                    </TableCell>
                    {/* Order number */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.orderNumber || ''}
                          onChange={(value) => handleUpdate(record.id!, 'orderNumber', value)}
                          placeholder="Order number"
                          recordId={record.id}
                          field="orderNumber"
                          pendingChanges={pendingChanges}
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.orderNumber || '-'}</div>
                      )}
                    </TableCell>
                    {/* Contract type */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.contractType || ''}
                          onChange={(value) => handleUpdate(record.id!, 'contractType', value)}
                          placeholder="Select contract type"
                          options={['Subcontract', 'Supply Only', 'Labour Only', 'CDP']}
                          recordId={record.id}
                          field="contractType"
                          pendingChanges={pendingChanges}
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.contractType || '-'}</div>
                      )}
                    </TableCell>
                    {/* Tender Manager */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.tenderManager || ''}
                          onChange={(value) => handleUpdate(record.id!, 'tenderManager', value)}
                          placeholder="Tender Manager"
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.tenderManager || '-'}</div>
                      )}
                    </TableCell>
                    {/* Tender Draft Start - 只读 */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <div className="text-xs text-gray-900 leading-tight">{record.tenderDraftStart || '-'}</div>
                    </TableCell>
                    {/* Tender Submit Date - 只读 */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <div className="text-xs text-gray-900 leading-tight">{record.tenderSubmitDate || '-'}</div>
                    </TableCell>
                    {/* Tender Period - 只读，通过计算得出 */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <div className="text-xs text-gray-900 leading-tight">
                        {(() => {
                          // 计算 Tender Period: tenderReturnDeadline - tenderSubmitDate
                          if (record.tenderReturnDeadline && record.tenderSubmitDate) {
                            const submitDate = new Date(record.tenderSubmitDate);
                            const deadlineDate = new Date(record.tenderReturnDeadline);
                            const diffTime = deadlineDate.getTime() - submitDate.getTime();
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return `${diffDays} days`;
                          }
                          return '-';
                        })()}
                      </div>
                    </TableCell>
                    {/* Tender Return Deadline - 只读 */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <div className="text-xs text-gray-900 leading-tight">{record.tenderReturnDeadline || '-'}</div>
                    </TableCell>
                    {/* Review Period - 计算字段：Contract & Order - Return Deadline */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      <div className="text-xs text-gray-900 leading-tight">
                        {(() => {
                          // 计算 Review Period: contractOrder - tenderReturnDeadline
                          if (record.contractOrder && record.tenderReturnDeadline) {
                            const returnDate = new Date(record.tenderReturnDeadline);
                            const contractDate = new Date(record.contractOrder);
                            const diffTime = contractDate.getTime() - returnDate.getTime();
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return `${diffDays} days`;
                          }
                          return '-';
                        })()}
                      </div>
                    </TableCell>
                    {/* Contract & Order */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.contractOrder || ''}
                          onChange={(value) => handleUpdate(record.id!, 'contractOrder', value)}
                          placeholder="Contract & Order"
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.contractOrder || '-'}</div>
                      )}
                    </TableCell>
                    {/* Lead Time */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.subcontractorPreparationLeadInPeriod || ''}
                          onChange={(value) => handleUpdate(record.id!, 'subcontractorPreparationLeadInPeriod', value)}
                          placeholder="Lead Time"
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.subcontractorPreparationLeadInPeriod || '-'}</div>
                      )}
                    </TableCell>
                    {/* Planned Site Start Date */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.originalSiteStartDate || ''}
                          onChange={(value) => handleUpdate(record.id!, 'originalSiteStartDate', value)}
                          type="date"
                          placeholder="Planned Site Start Date"
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.originalSiteStartDate || '-'}</div>
                      )}
                    </TableCell>
                    {/* Actual Site Start Date */}
                    <TableCell className="py-2 px-3 text-center align-middle">
                      {isEditing ? (
                        <EditableCell
                          value={record.actualSiteStartDate || ''}
                          onChange={(value) => handleUpdate(record.id!, 'actualSiteStartDate', value)}
                          type="date"
                          placeholder="Actual Site Start Date"
                        />
                      ) : (
                        <div className="text-xs text-gray-900 leading-tight">{record.actualSiteStartDate || '-'}</div>
                      )}
                    </TableCell>
                    {isEditing && (
                      <TableCell className="py-2 px-3">
                        <div className="flex justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(record.id!)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  
                  {/* 计划数据行 - 仅在 Plan VS Actual 模式下显示 */}
                  {showPlanVsActual && (
                    <TableRow key={`planned-${record.id || index}-${index}`} className="border-b border-gray-200 bg-blue-50 hover:bg-blue-100 transition-colors h-16">
                      {/* 合并的列 - Tender Package, Status, Order Number, Contract Type, Tender Manager */}
                      <TableCell className="py-2 px-3 text-center align-middle" colSpan={5}>
                        <div className="flex flex-col space-y-1">
                          <div className="font-medium text-blue-700 text-xs leading-tight">
                            📅 {record.tenderPackage || '-'} (Planned)
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {record.programmeStatus || 'Programme Status'}
                            </span>
                            {record.orderNumber && (
                              <span className="text-xs text-blue-600">#{record.orderNumber}</span>
                            )}
                            {record.contractType && (
                              <span className="text-xs text-blue-600">• {record.contractType}</span>
                            )}
                            {record.tenderManager && (
                              <span className="text-xs text-blue-600">• {record.tenderManager}</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      
                      {/* Draft Start - 空 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        <div className="text-xs text-gray-500 leading-tight">-</div>
                      </TableCell>
                      
                      {/* Planned Submit Date - 计算得出，不可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        <div className="text-xs text-blue-600 leading-tight">
                          {(() => {
                            const plannedDates = calculatePlannedDates(record);
                            return plannedDates?.plannedSubmitDate || '-';
                          })()}
                        </div>
                      </TableCell>
                      
                      {/* Planned Tender Period - 可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        {isEditing ? (
                          <EditableCell
                            value={record.plannedSiteStart ? (record.plannedTenderPeriod || 14) : ''}
                            onChange={(value) => handleUpdate(record.id!, 'plannedTenderPeriod', value)}
                            type="number"
                            placeholder="14"
                            recordId={record.id}
                            field="plannedTenderPeriod"
                            pendingChanges={pendingChanges}
                            key={`${record.id}-plannedTenderPeriod-${record.plannedTenderPeriod}`}
                            forceValue={pendingChanges.has(record.id!) ? pendingChanges.get(record.id!)?.plannedTenderPeriod : undefined}
                          />
                        ) : (
                          <div className="text-xs text-blue-700 leading-tight font-medium">
                            {record.plannedSiteStart ? (record.plannedTenderPeriod || 14) + ' days' : '-'}
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Planned Return Deadline - 计算得出，不可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        <div className="text-xs text-blue-600 leading-tight">
                          {(() => {
                            const plannedDates = calculatePlannedDates(record);
                            return plannedDates?.plannedReturnDeadline || '-';
                          })()}
                        </div>
                      </TableCell>
                      
                      {/* Planned Review Period - 可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        {isEditing ? (
                          <EditableCell
                            value={record.plannedSiteStart ? (record.plannedReviewPeriod || 14) : ''}
                            onChange={(value) => handleUpdate(record.id!, 'plannedReviewPeriod', value)}
                            type="number"
                            placeholder="14"
                            recordId={record.id}
                            field="plannedReviewPeriod"
                            pendingChanges={pendingChanges}
                            key={`${record.id}-plannedReviewPeriod-${record.plannedReviewPeriod}`}
                            forceValue={pendingChanges.has(record.id!) ? pendingChanges.get(record.id!)?.plannedReviewPeriod : undefined}
                          />
                        ) : (
                          <div className="text-xs text-blue-700 leading-tight font-medium">
                            {record.plannedSiteStart ? (record.plannedReviewPeriod || 14) + ' days' : '-'}
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Planned Contract & Order - 计算得出，不可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        <div className="text-xs text-blue-600 leading-tight">
                          {(() => {
                            const plannedDates = calculatePlannedDates(record);
                            return plannedDates?.plannedContractOrder || '-';
                          })()}
                        </div>
                      </TableCell>
                      
                      {/* Planned Lead Time - 可编辑 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        {isEditing ? (
                          <EditableCell
                            value={record.plannedSiteStart ? String(record.plannedLeadTime || 60) : ''}
                            onChange={(value) => handleUpdate(record.id!, 'plannedLeadTime', value)}
                            type="number"
                            placeholder="60"
                            recordId={record.id}
                            field="plannedLeadTime"
                            pendingChanges={pendingChanges}
                            key={`${record.id}-plannedLeadTime-${record.plannedLeadTime}`}
                            forceValue={pendingChanges.has(record.id!) ? pendingChanges.get(record.id!)?.plannedLeadTime : undefined}
                          />
                        ) : (
                          <div className="text-xs text-blue-700 leading-tight font-medium">
                            {record.plannedSiteStart ? String(record.plannedLeadTime || 60) + ' days' : '-'}
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Planned Site Start - 可编辑，独立数据 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        {isEditing ? (
                          <EditableCell
                            value={record.plannedSiteStart || ''}
                            onChange={(value) => {
                              handleUpdate(record.id!, 'plannedSiteStart', value);
                            }}
                            type="date"
                            placeholder="Planned Site Start"
                          />
                        ) : (
                          <div className="text-xs text-blue-700 leading-tight font-medium">
                            {record.plannedSiteStart || '-'}
                          </div>
                        )}
                      </TableCell>
                      
                      {/* Actual Site Start - 显示实际数据 */}
                      <TableCell className="py-2 px-3 text-center align-middle">
                        <div className="text-xs text-blue-600 leading-tight">
                          {record.actualSiteStartDate || '-'}
                        </div>
                      </TableCell>
                      
                      {/* Actions - 空 */}
                      {isEditing && (
                        <TableCell className="py-2 px-3 text-center align-middle">
                          <div className="text-xs text-gray-400">-</div>
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}